import { supabase } from '@/lib/supabase'
import { useEffect, useRef, useState } from 'react'
import '@/styles/landing.css'

const COMPETENCIES = [
  { name: 'Leadership', desc: 'Can you influence, direct, and inspire — even without a title?' },
  { name: 'Teamwork', desc: 'Do you collaborate, support, and navigate conflict constructively?' },
  {
    name: 'Communication',
    desc: 'Are you clear, concise, and adaptable across different audiences?',
  },
  {
    name: 'Problem Solving',
    desc: 'How do you approach ambiguity and structure thinking under pressure?',
  },
  {
    name: 'Time Management',
    desc: 'Can you prioritize, scope, and deliver when everything feels urgent?',
  },
  {
    name: 'Stakeholder Management',
    desc: 'Do you know who matters, and how to keep them aligned?',
  },
  {
    name: 'Attention to Detail',
    desc: 'Do you catch errors and inconsistencies before they become problems?',
  },
]

const TICKER_ITEMS = [
  'STAR Method Scoring',
  'Adaptive Follow-ups',
  'Role-specific Questions',
  'Post-Interview Reports',
  'Competency Progress Tracking',
  'Finance & Consulting Ready',
  '3 Free Sessions Every 30 Days',
]

function MockUI() {
  return (
    <div className="mock-ui">
      <div className="mock-header">
        <div className="mock-dots">
          <div className="dot dot-r" />
          <div className="dot dot-y" />
          <div className="dot dot-g" />
        </div>
        <div className="mock-title">roleround.com — Interview Session</div>
        <div className="mock-badge">● LIVE</div>
      </div>

      <div className="mock-body">
        <div className="chat-row">
          <div className="chat-avatar ai">RR</div>
          <div className="chat-bubble ai">
            <div className="q-label">Question 3 of 8</div>
            Tell me about a time you had to lead a project when the outcome was uncertain. How did
            you handle it?
          </div>
        </div>

        <div className="chat-row user">
          <div className="chat-avatar me">Me</div>
          <div className="chat-bubble user">
            During my junior year, I led our engineering club's robotics competition team. We had 6
            weeks and had never competed before. I reorganized the workflow into 2-day sprints, ran
            daily standups, and we placed 2nd out of 18 teams.
          </div>
        </div>

        <div className="chat-row">
          <div className="chat-avatar ai">RR</div>
          <div className="chat-bubble ai">
            Good structure. Your <strong>Action</strong> is strong — can you quantify the outcome
            more specifically? What metric improved from your sprint approach?
          </div>
        </div>

        <div className="star-breakdown">
          <div className="star-header">STAR Score — Real-time Evaluation</div>
          <div className="star-body">
            <div className="star-row">
              <div className="star-label">Situation</div>
              <div className="star-bar">
                <div className="star-fill" style={{ width: '88%', animationDelay: '0.1s' }} />
              </div>
              <div className="star-score">88%</div>
            </div>
            <div className="star-row">
              <div className="star-label">Task</div>
              <div className="star-bar">
                <div className="star-fill" style={{ width: '72%', animationDelay: '0.25s' }} />
              </div>
              <div className="star-score">72%</div>
            </div>
            <div className="star-row">
              <div className="star-label">Action</div>
              <div className="star-bar">
                <div className="star-fill" style={{ width: '91%', animationDelay: '0.4s' }} />
              </div>
              <div className="star-score">91%</div>
            </div>
            <div className="star-row">
              <div className="star-label">Result</div>
              <div className="star-bar">
                <div
                  className="star-fill orange"
                  style={{ width: '54%', animationDelay: '0.55s' }}
                />
              </div>
              <div className="star-score">54%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mock-footer">
        <div className="mock-input">Type your response...</div>
        <div className="mock-send">Send →</div>
      </div>
    </div>
  )
}

interface PopularRole {
  role: string
  session_count: number
}

export default function LandingPage() {
  const cursorRef = useRef<HTMLDivElement>(null)
  const trailRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [popularRoles, setPopularRoles] = useState<PopularRole[]>([])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session)
    })
  }, [])

  useEffect(() => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/popular-roles`
    fetch(url, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
    })
      .then((r) => r.json())
      .then((data: PopularRole[]) => {
        if (Array.isArray(data) && data.length > 0) setPopularRoles(data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const cursor = cursorRef.current
    const trail = trailRef.current
    const nav = navRef.current
    if (!cursor || !trail) return
    if (window.matchMedia('(hover: none)').matches) return

    let mx = 0
    let my = 0
    let trailX = 0
    let trailY = 0
    let rafId: number

    function onMouseMove(e: MouseEvent) {
      mx = e.clientX
      my = e.clientY
      if (cursor) cursor.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%)`
    }

    function animateTrail() {
      trailX += (mx - trailX) * 0.18
      trailY += (my - trailY) * 0.18
      if (trail) trail.style.transform = `translate(${trailX}px, ${trailY}px) translate(-50%, -50%)`
      rafId = requestAnimationFrame(animateTrail)
    }

    animateTrail()

    function onScroll() {
      nav?.classList.toggle('scrolled', window.scrollY > 30)
    }

    document.addEventListener('mousemove', onMouseMove)
    window.addEventListener('scroll', onScroll)

    const interactives = document.querySelectorAll<HTMLElement>(
      'a, button, .step, .lp-star-component, .plan, .competency-item'
    )
    // biome-ignore lint/complexity/noForEach: NodeList.forEach is appropriate for DOM iteration
    interactives.forEach((el) => {
      el.addEventListener('mouseenter', () => cursor?.classList.add('hover'))
      el.addEventListener('mouseleave', () => cursor?.classList.remove('hover'))
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement
            el.style.transitionDelay = `${i * 0.08}s`
            el.classList.add('visible')
          }
        })
      },
      { threshold: 0.1 }
    )
    // biome-ignore lint/complexity/noForEach: NodeList.forEach is appropriate for DOM iteration
    document
      .querySelectorAll('.step, .lp-star-component, .plan, .competency-item')
      .forEach((el) => observer.observe(el))

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(rafId)
      observer.disconnect()
    }
  }, [])

  return (
    <div className="landing-page">
      <div className="cursor" ref={cursorRef} />
      <div className="cursor-trail" ref={trailRef} />

      {/* Nav */}
      <nav ref={navRef}>
        <a href="/" className="nav-logo">
          Role<span>Round</span>
        </a>
        <div className="nav-links">
          <a href="#how">How It Works</a>
          <a href="#star">STAR Method</a>
          <a href="#competencies">Competencies</a>
          <a href="#pricing">Pricing</a>
          <a href="/sample-reports">Sample Reports</a>
          {!isLoggedIn && (
            <a href="/login" className="nav-login">
              Log in
            </a>
          )}
          <a href={isLoggedIn ? '/dashboard' : '/login'} className="btn-nav">
            {isLoggedIn ? 'Go to Dashboard' : 'Start Free'}
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-grid-line" />
        <div className="hero-grid-line" />
        <div className="hero-grid-line" />

        <div className="hero-left">
          <div className="hero-badge">Behavioral Interview Coaching</div>

          <h1 className="hero-headline">
            Ace the
            <em>behavioral</em>
            round.
          </h1>

          <p className="hero-sub">
            Most students walk into behavioral interviews unprepared — one career-center mock per
            semester, a PDF about STAR, and a roommate who says "yeah, that sounded fine." RoleRound
            lets you practice real behavioral interviews with adaptive follow-ups and honest scored
            feedback.
          </p>

          <p className="hero-fix-line">Know exactly what to fix before the real interview.</p>

          <div className="hero-ctas">
            <a href="/login" className="btn-primary">
              Practice Free →
            </a>
            <a href="#how" className="btn-ghost">
              See How It Works
            </a>
          </div>

          <a href="/sample-reports" className="mock-report-link">
            See a sample report →
          </a>

          <div className="hero-social-proof">
            <div className="avatars">
              <div className="avatar">JK</div>
              <div className="avatar">ML</div>
              <div className="avatar">RP</div>
              <div className="avatar">AS</div>
            </div>
            <div className="social-text">
              <strong>3 free sessions every 30 days.</strong> No credit card needed.
            </div>
          </div>
        </div>

        <div className="hero-right">
          <div className="mock-caption">See how your answers score — and how to improve them.</div>
          <MockUI />
          <a href="/sample-reports" className="mock-report-link">
            See a real finished report →
          </a>
        </div>
      </section>

      {/* Ticker */}
      <div className="ticker">
        <div className="ticker-inner">
          {[
            ...TICKER_ITEMS.map((item) => ({ item, half: 'a' })),
            ...TICKER_ITEMS.map((item) => ({ item, half: 'b' })),
          ].map(({ item, half }) => (
            <div key={`${half}-${item}`} className="ticker-item">
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <section className="section" id="how">
        <div className="section-label">How It Works</div>
        <h2 className="section-title">
          Three steps to
          <br />
          <em>sounding like you've done this before.</em>
        </h2>

        <div className="steps">
          <div className="step">
            <div className="step-num">01 / Setup</div>
            <span className="step-icon">🎯</span>
            <h3>Pick your role & level</h3>
            <p>
              Tell RoleRound the job title and your experience level. Questions are calibrated — a
              sophomore applying for a summer internship gets different prompts than a senior
              gunning for consulting.
            </p>
          </div>
          <div className="step">
            <div className="step-num">02 / Interview</div>
            <span className="step-icon">💬</span>
            <h3>Face 8–10 real behavioral questions</h3>
            <p>
              The AI interviewer asks, listens, and follows up — just like a real recruiter would.
              Weak on your Result? Expect a "what was the measurable outcome?" before moving on. Max
              2 follow-ups per question.
            </p>
          </div>
          <div className="step">
            <div className="step-num">03 / Report</div>
            <span className="step-icon">📊</span>
            <h3>Get a scored report with rewrites</h3>
            <p>
              Every session ends with a full breakdown: STAR scores for each answer, specific
              improvement suggestions, and an example of how a strong answer would read. No vague
              "good job" — just real feedback.
            </p>
          </div>
        </div>
      </section>

      {/* Visual Evidence */}
      <section className="section evidence-section">
        <div className="section-label">Real Feedback</div>
        <h2 className="section-title">
          Know exactly what to fix
          <br />
          <em>before the real interview.</em>
        </h2>
        <div className="evidence-grid">
          <div className="evidence-card">
            <div className="evidence-card-header">
              <div className="evidence-dots">
                <div className="dot dot-r" />
                <div className="dot dot-y" />
                <div className="dot dot-g" />
              </div>
              <div className="evidence-card-title">STAR Score Breakdown</div>
            </div>
            <div className="evidence-card-body">
              <div className="evidence-overall">
                <span className="evidence-score">3.8</span>
                <span className="evidence-score-denom"> / 5.0</span>
                <span className="evidence-score-label">Overall</span>
              </div>
              <div className="evidence-bars">
                <div className="evidence-bar-row">
                  <div className="evidence-bar-label">Situation</div>
                  <div className="evidence-bar-track">
                    <div className="evidence-bar-fill" style={{ width: '72%' }} />
                  </div>
                  <div className="evidence-bar-pct">72%</div>
                </div>
                <div className="evidence-bar-row">
                  <div className="evidence-bar-label">Task</div>
                  <div className="evidence-bar-track">
                    <div className="evidence-bar-fill" style={{ width: '68%' }} />
                  </div>
                  <div className="evidence-bar-pct">68%</div>
                </div>
                <div className="evidence-bar-row">
                  <div className="evidence-bar-label">Action</div>
                  <div className="evidence-bar-track">
                    <div className="evidence-bar-fill lime" style={{ width: '91%' }} />
                  </div>
                  <div className="evidence-bar-pct lime">91% ↑</div>
                </div>
                <div className="evidence-bar-row">
                  <div className="evidence-bar-label">Result</div>
                  <div className="evidence-bar-track">
                    <div className="evidence-bar-fill orange" style={{ width: '54%' }} />
                  </div>
                  <div className="evidence-bar-pct orange">54% ↓</div>
                </div>
              </div>
              <div className="evidence-tag">Your strongest: Action · Work on: Result</div>
            </div>
            <div className="evidence-card-caption">Every answer scored instantly.</div>
          </div>

          <div className="evidence-card">
            <div className="evidence-card-header">
              <div className="evidence-dots">
                <div className="dot dot-r" />
                <div className="dot dot-y" />
                <div className="dot dot-g" />
              </div>
              <div className="evidence-card-title">Your Feedback Report</div>
            </div>
            <div className="evidence-card-body">
              <div className="evidence-report-section">
                <div className="evidence-report-label">Strengths</div>
                <div className="evidence-report-item">
                  ✓ Strong <strong>Action</strong> section — specific, first-person, sequenced
                  clearly
                </div>
              </div>
              <div className="evidence-report-section">
                <div className="evidence-report-label">Improve</div>
                <div className="evidence-report-item">
                  ↑ <strong>Result</strong> needs a quantified outcome — "we improved" isn't enough
                </div>
              </div>
              <div className="evidence-report-section">
                <div className="evidence-report-label">Rewrite tip</div>
                <div className="evidence-report-item">
                  Add the actual metric: response time, error rate, revenue impact, or team size.
                </div>
              </div>
              <div className="evidence-report-gated">
                <div className="evidence-report-blur">
                  See what a 4.5 answer looks like — full rewrite example with Pro
                </div>
                <a href="/login" className="evidence-report-upgrade">
                  Upgrade to see example →
                </a>
              </div>
            </div>
            <div className="evidence-card-caption">Know exactly what to fix.</div>
          </div>
        </div>
      </section>

      {/* STAR Method */}
      <section className="section star-section" id="star">
        <div className="star-grid">
          <div>
            <div className="section-label">The Framework</div>
            <h2 className="section-title">
              Built around
              <br />
              <em>STAR.</em>
            </h2>
            <p className="star-intro-text">
              Every behavioral answer is graded on the four components that recruiters actually care
              about — weighted by what matters most. You'll see exactly where your stories are
              strong and where they collapse.
            </p>
          </div>

          <div className="star-components">
            <div className="lp-star-component">
              <div className="star-letter">S</div>
              <div className="star-content">
                <h4>Situation</h4>
                <p>Set the scene. Enough context to make the story meaningful — not a novel.</p>
              </div>
              <div className="star-weight">20%</div>
            </div>
            <div className="lp-star-component">
              <div className="star-letter">T</div>
              <div className="star-content">
                <h4>Task</h4>
                <p>
                  Your specific role and responsibility. What were you personally accountable for?
                </p>
              </div>
              <div className="star-weight">15%</div>
            </div>
            <div className="lp-star-component">
              <div className="star-letter">A</div>
              <div className="star-content">
                <h4>Action</h4>
                <p>
                  The meat. What did <em>you</em> specifically do? Leadership answers die here when
                  they're too vague.
                </p>
              </div>
              <div className="star-weight">40%</div>
            </div>
            <div className="lp-star-component">
              <div className="star-letter">R</div>
              <div className="star-content">
                <h4>Result</h4>
                <p>
                  Quantified outcomes preferred. "We improved things" is a 54% — "we cut time by
                  30%" is a 91%.
                </p>
              </div>
              <div className="star-weight">25%</div>
            </div>
          </div>
        </div>
      </section>

      {/* Competencies */}
      <section className="section" id="competencies">
        <div className="competencies-grid">
          <div>
            <div className="section-label">Competencies</div>
            <h2 className="section-title">
              Know your strengths,
              <br />
              <em>own the room.</em>
            </h2>
            <p className="competencies-intro">
              Recruiters test specific behavioral competencies — not generic "tell me about
              yourself" rambles. RoleRound targets the exact competency your role demands, so every
              session builds the muscle that actually gets you hired.
            </p>
          </div>

          <div className="competency-list">
            {COMPETENCIES.map((c) => (
              <div key={c.name} className="competency-item">
                <div className="competency-name">{c.name}</div>
                <div className="competency-desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Practiced Roles */}
      {popularRoles.length > 0 && (
        <section className="section roles-section">
          <div className="section-label">Community</div>
          <h2 className="section-title">
            What people are
            <br />
            <em>practicing.</em>
          </h2>
          <div className="roles-grid">
            {popularRoles.map((r) => (
              <div key={r.role} className="role-chip">
                <span className="role-chip-name">{r.role}</span>
                <span className="role-chip-count">{r.session_count.toLocaleString()} sessions</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pricing */}
      <section className="section" id="pricing">
        <div className="section-label">Pricing</div>
        <h2 className="section-title">
          Priced for
          <br />
          <em>students,</em> not consultants.
        </h2>

        <div className="pricing-grid">
          <div className="plan">
            <div className="plan-name">Free</div>
            <div className="plan-price">
              $0<span>/mo</span>
            </div>
            <div className="plan-desc">No credit card. No excuses.</div>
            <div className="plan-divider" />
            <ul className="plan-features">
              <li>
                <strong>3 sessions every 30 days</strong>
              </li>
              <li>STAR scoring on every answer</li>
              <li>Adaptive follow-up questions</li>
              <li>Post-interview feedback report</li>
              <li>Session history (last 3)</li>
              <li>Per-session competency scores</li>
              <li>Drill Mode practice</li>
              <li>Job description upload</li>
              <li>Story Bank (3 stories)</li>
              <li className="dim">Unlimited sessions</li>
              <li className="dim">Unlimited session history</li>
              <li className="dim">Competency trend dashboard</li>
            </ul>
            <a href="/login" className="btn-plan secondary">
              Get Started Free
            </a>
          </div>

          <div className="plan featured">
            <div className="plan-name">Pro</div>
            <div className="plan-price">
              $8<span>/mo</span>
            </div>
            <div className="plan-desc">Or $59/yr — that's $4.90/month.</div>
            <div className="plan-divider" />
            <ul className="plan-features">
              <li>Unlimited interview sessions</li>
              <li>STAR scoring on every answer</li>
              <li>Adaptive follow-up questions</li>
              <li>Post-interview feedback report</li>
              <li>Per-session competency scores</li>
              <li>Full answer review after every session</li>
              <li>Unlimited session history</li>
              <li>Competency trend dashboard</li>
              <li>Answer Rewrite Coach</li>
              <li>Unlimited Story Bank + story editing</li>
              <li>Priority access to new features</li>
            </ul>
            <a href="/login" className="btn-plan primary">
              Go Pro →
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <h2>
          Stop winging it.
          <br />
          <em>Start practicing.</em>
        </h2>
        <p>Your roommate says "yeah that sounded fine." We won't.</p>
        <a href="/login" className="btn-primary">
          Start Free → No Card Required
        </a>
      </section>

      {/* Footer */}
      <footer>
        <a href="/" className="footer-logo">
          Role<span>Round</span>
        </a>
        <div className="footer-links">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/contact">Contact</a>
          <a href="/partnerships">Partnerships</a>
          <a href="/sample-reports">Sample Reports</a>
        </div>
        <div className="footer-copy">© 2026 RoleRound. All rights reserved.</div>
      </footer>
    </div>
  )
}
